/**
 * ClientStatePricingSection — SystemAdmin per-org, per-state pricing for state DMV
 * (MVR) lookups. One row per state: the standard base price, an optional org-specific
 * price override, an optional discount %, and an MVR enabled toggle.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DollarSign, Loader2, RefreshCw, Save, Search, ChevronDown } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  listClientStatePrices, saveClientStatePrices,
  ClientStatePrice, StatePriceItem,
} from '../../services/clientsAdminService';

interface RowEdit {
  orgPrice: string;        // raw input; '' = use standard base
  discountPercent: string; // raw input; '' or '0' = no discount
  isActive: boolean;
  mvrEnabled: boolean;
}

const money = (n: number) => `$${n.toFixed(2)}`;

// Effective price preview: (orgPrice or base) minus discount %, floored at 0.
const computeEffective = (base: number, orgPriceRaw: string, discountRaw: string): number => {
  const op = orgPriceRaw.trim() === '' ? base : parseFloat(orgPriceRaw);
  const price = Number.isFinite(op) ? op : base;
  const d = discountRaw.trim() === '' ? 0 : parseFloat(discountRaw);
  const disc = Number.isFinite(d) ? d : 0;
  const eff = price * (1 - disc / 100);
  return Math.max(0, Math.round(eff * 100) / 100);
};

const toEdits = (rows: ClientStatePrice[]): Record<string, RowEdit> => {
  const out: Record<string, RowEdit> = {};
  for (const r of rows) {
    out[r.stateCode] = {
      orgPrice: r.orgPrice == null ? '' : r.orgPrice.toFixed(2),
      discountPercent: r.discountPercent == null || r.discountPercent === 0 ? '' : String(r.discountPercent),
      isActive: r.isActive,
      mvrEnabled: r.mvrEnabled,
    };
  }
  return out;
};

const ClientStatePricingSection: React.FC<{ clientId: string; defaultOpen?: boolean }> = ({ clientId, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState<ClientStatePrice[]>([]);
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyCustom, setOnlyCustom] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listClientStatePrices(clientId);
      setRows(data);
      setEdits(toEdits(data));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (code: string, patch: Partial<RowEdit>) =>
    setEdits((p) => ({ ...p, [code]: { ...p[code], ...patch } }));

  const dirty = useMemo(() => {
    const base = toEdits(rows);
    return rows.some((r) => {
      const a = base[r.stateCode], b = edits[r.stateCode];
      if (!b) return false;
      return (
        a.orgPrice !== b.orgPrice ||
        a.discountPercent !== b.discountPercent ||
        a.isActive !== b.isActive ||
        a.mvrEnabled !== b.mvrEnabled
      );
    });
  }, [rows, edits]);

  const isRowCustom = (r: ClientStatePrice) => {
    const e = edits[r.stateCode];
    if (!e) return false;
    return (
      e.orgPrice.trim() !== '' ||
      (e.discountPercent.trim() !== '' && e.discountPercent.trim() !== '0') ||
      !e.mvrEnabled
    );
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.stateName.toLowerCase().includes(q) && !r.stateCode.toLowerCase().includes(q)) return false;
      if (onlyCustom && !isRowCustom(r)) return false;
      return true;
    });
  }, [rows, edits, search, onlyCustom]); // eslint-disable-line react-hooks/exhaustive-deps

  const customCount = useMemo(() => rows.filter(isRowCustom).length, [rows, edits]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    // Validate
    for (const r of rows) {
      const e = edits[r.stateCode];
      if (!e) continue;
      if (e.orgPrice.trim() !== '') {
        const v = parseFloat(e.orgPrice);
        if (!Number.isFinite(v) || v < 0) { setError(`Price for ${r.stateName} must be 0 or more.`); return; }
      }
      if (e.discountPercent.trim() !== '') {
        const v = parseFloat(e.discountPercent);
        if (!Number.isFinite(v) || v < 0 || v > 100) { setError(`Discount for ${r.stateName} must be 0–100.`); return; }
      }
    }

    const items: StatePriceItem[] = [];
    for (const r of rows) {
      const e = edits[r.stateCode];
      if (!e) continue;
      const hasPrice = e.orgPrice.trim() !== '';
      const hasDisc = e.discountPercent.trim() !== '' && parseFloat(e.discountPercent) > 0;
      // Include row if it has any customization (price, discount, or MVR disabled)
      if (!hasPrice && !hasDisc && e.mvrEnabled) continue;
      items.push({
        stateCode: r.stateCode,
        orgPrice: hasPrice ? parseFloat(e.orgPrice) : null,
        discountPercent: hasDisc ? parseFloat(e.discountPercent) : null,
        isActive: e.isActive,
        mvrEnabled: e.mvrEnabled,
      });
    }

    setSaving(true); setError('');
    try {
      const updated = await saveClientStatePrices(clientId, items);
      setRows(updated);
      setEdits(toEdits(updated));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
      <div
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between px-6 py-4 bg-black/[0.02] hover:bg-black/[0.04] transition-colors cursor-pointer ${open ? 'border-b border-black/[0.08]' : ''}`}
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${open ? '' : '-rotate-90'}`} />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#c41e3a]" /> Per-State Pricing
            {!open && customCount > 0 && <span className="text-xs font-medium text-[#c41e3a] normal-case tracking-normal">({customCount} custom)</span>}
          </h2>
        </div>
        <button onClick={(e) => { e.stopPropagation(); load(); }} className="text-xs text-[#64748b] hover:text-[#c41e3a] inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {open && (
      <div className="px-6 py-5">
      <p className="text-xs text-[#64748b] mb-4">
        Org-specific price for state DMV lookups, then an optional discount. Leave a state's price blank to use the standard base. Uncheck MVR to block lookups for that state. {customCount > 0 && <span className="font-medium text-[#0f172a]">{customCount} custom.</span>}
      </p>

      {error && <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 text-[#94a3b8] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search states…"
            className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50" />
        </div>
        <label className="text-xs text-[#475569] inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={onlyCustom} onChange={(e) => setOnlyCustom(e.target.checked)} /> Only custom
        </label>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading pricing…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
              <tr>
                <th className="text-left py-2">State</th>
                <th className="text-right">Standard</th>
                <th className="text-right">Org Price</th>
                <th className="text-right">Discount %</th>
                <th className="text-right">Effective</th>
                <th className="text-center pl-6 border-l border-black/[0.08]">Allow MVR</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const e = edits[r.stateCode] ?? { orgPrice: '', discountPercent: '', isActive: true, mvrEnabled: false };
                const eff = computeEffective(r.standardBasePrice, e.orgPrice, e.discountPercent);
                const custom = e.orgPrice.trim() !== '' || (e.discountPercent.trim() !== '' && e.discountPercent.trim() !== '0');
                return (
                  <tr key={r.stateCode} className={`border-b border-black/[0.04] ${!e.mvrEnabled ? 'opacity-50' : ''}`}>
                    <td className="py-2 text-[#0f172a] font-medium">
                      {r.stateName} <span className="text-[#94a3b8] font-mono text-xs">{r.stateCode}</span>
                      {!e.mvrEnabled && <span className="ml-2 text-xs font-normal text-red-500">blocked</span>}
                    </td>
                    <td className="text-right text-[#94a3b8]">{money(r.standardBasePrice)}</td>
                    <td className="text-right">
                      <div className="inline-flex items-center">
                        <span className="text-[#94a3b8] mr-0.5">$</span>
                        <input type="text" inputMode="decimal" value={e.orgPrice}
                          onChange={(ev) => setEdit(r.stateCode, { orgPrice: ev.target.value })}
                          onBlur={(ev) => { const v = ev.target.value.trim(); if (v !== '' && Number.isFinite(parseFloat(v))) setEdit(r.stateCode, { orgPrice: parseFloat(v).toFixed(2) }); }}
                          placeholder={r.standardBasePrice.toFixed(2)}
                          className="w-20 px-2 py-1 rounded border border-black/[0.1] text-right text-sm focus:outline-none focus:border-[#c41e3a]/50" />
                      </div>
                    </td>
                    <td className="text-right">
                      <input type="number" min={0} max={100} step="0.5" value={e.discountPercent}
                        onChange={(ev) => setEdit(r.stateCode, { discountPercent: ev.target.value })}
                        placeholder="0"
                        className="w-16 px-2 py-1 rounded border border-black/[0.1] text-right text-sm focus:outline-none focus:border-[#c41e3a]/50" />
                    </td>
                    <td className={`text-right font-semibold ${custom ? 'text-[#c41e3a]' : 'text-[#0f172a]'}`}>{money(eff)}</td>
                    <td className="text-center pl-6 border-l border-black/[0.08]">
                      <input type="checkbox" checked={e.mvrEnabled}
                        onChange={(ev) => setEdit(r.stateCode, { mvrEnabled: ev.target.checked })} />
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[#94a3b8]">No states match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button onClick={handleSave} disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save pricing
        </button>
      </div>
      </div>
      )}
    </div>
  );
};

export default ClientStatePricingSection;
